import React, { useState, useEffect, useRef } from "react";
import { Box, Container, Typography, Stack, Button, Chip } from "@mui/material";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StorageIcon from "@mui/icons-material/Storage";
import VerifiedIcon from "@mui/icons-material/Verified";
import GroupsIcon from "@mui/icons-material/Groups";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import LockIcon from "@mui/icons-material/Lock";
import ShieldIcon from "@mui/icons-material/Shield";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import useSEO from "../../hooks/useSEO";

// Social proof images
import pic1 from "./assets/pic1.jpg";
import pic2 from "./assets/pic2.jpg";
import pic3 from "./assets/pic3.jpg";
import pic4 from "./assets/pic4.jpg";

// Mentor photo placeholders — replace with real imports when available
// import srikanth from "./assets/srikanth.jpg";
// import karthik  from "./assets/karthik.jpg";
// import sahas    from "./assets/sahas.jpg";

// ─── Design tokens ────────────────────────────────────────────────────────────
const MONO    = "'JetBrains Mono', 'Fira Code', ui-monospace, monospace";
const ACCENT  = "#E2684A";
const ACCENT2 = "#F3C969";
const ACCENT_SOFT = "rgba(226, 104, 74, 0.12)";
const PURPLE  = "#7C3AED";
const CYAN    = "#22D3EE";
const BG      = "#030014";
const BG2     = "#0F0728";
const LINE    = "#1E2130";
const INK     = "#E7E9EE";
const INK2    = "#B6BBC6";
const INK3    = "#7D8390";
const GOOD    = "#8BD17C";
const R       = "14px";

// ─── Animation helper ─────────────────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay },
});

// ─── Shared primitives ────────────────────────────────────────────────────────
const Eyebrow = ({ children, center = false }) => (
  <Typography sx={{
    fontFamily: MONO, fontSize: "12px", letterSpacing: "0.16em",
    textTransform: "uppercase", color: ACCENT, mb: 1.75,
    textAlign: center ? "center" : "left",
  }}>
    {children}
  </Typography>
);

const SectionHead = ({ children, maxWidth = 820, center = false, sx = {} }) => (
  <Typography sx={{
    fontFamily: "'IBM Plex Serif', serif",
    fontSize: { xs: "1.45rem", md: "1.95rem" },
    fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.15,
    color: INK, maxWidth, mb: 2.5,
    mx: center ? "auto" : 0,
    textAlign: center ? "center" : "left",
    ...sx,
  }}>
    {children}
  </Typography>
);

const Body = ({ children, center = false, sx = {} }) => (
  <Typography sx={{
    color: INK2, lineHeight: 1.7, maxWidth: 760, fontSize: "1rem",
    mx: center ? "auto" : 0,
    textAlign: center ? "center" : "left",
    ...sx,
  }}>
    {children}
  </Typography>
);

const GradientText = ({ children, from = ACCENT, to = PURPLE }) => (
  <Box component="span" sx={{
    background: `linear-gradient(90deg, ${from} 0%, ${to} 100%)`,
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
  }}>
    {children}
  </Box>
);

const BookBtn = ({ label = "Book a scoping call", size = "md" }) => (
  <Button
    variant="contained"
    endIcon={<ArrowForwardIcon />}
    data-cal-link="nirman-agrawal/30-min-meeting"
    data-cal-namespace="30-min-meeting"
    data-cal-config='{"layout":"month_view","useSlotsViewOnSmallScreen":"true"}'
    sx={{
      fontFamily: MONO, fontSize: size === "lg" ? "15px" : "13.5px",
      px: size === "lg" ? 5 : 3, py: size === "lg" ? 1.8 : 1.4,
      borderRadius: "10px", cursor: "pointer",
      background: ACCENT, color: "#1a0f0a", boxShadow: "none",
      textTransform: "none",
      "&:hover": { background: "#e88564", boxShadow: "none" },
    }}
  >
    {label}
  </Button>
);

const OutputBadge = ({ text }) => (
  <Box sx={{
    display: "inline-flex", alignItems: "center", gap: 1,
    border: `1px solid rgba(226,104,74,0.25)`, borderRadius: 2,
    px: 2.5, py: 1.2,
    background: "rgba(226,104,74,0.06)",
  }}>
    <CheckCircleOutlineIcon sx={{ color: ACCENT, fontSize: "1rem" }} />
    <Typography sx={{ color: ACCENT, fontWeight: 600, fontSize: "13.5px" }}>{text}</Typography>
  </Box>
);

// ─── 1. HERO ──────────────────────────────────────────────────────────────────
const Hero = () => (
  <Box sx={{
    pt: { xs: 14, md: 16 }, pb: { xs: 8, md: 10 },
    borderBottom: `1px solid ${LINE}`,
    position: "relative", overflow: "hidden", background: BG,
  }}>
    <Box sx={{
      position: "absolute", inset: 0, pointerEvents: "none",
      background: `
        radial-gradient(800px 400px at 80% -10%, rgba(226,104,74,0.09), transparent 60%),
        radial-gradient(600px 400px at 0% 100%, rgba(243,201,105,0.05), transparent 60%)
      `,
    }} />
    <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
      <motion.div {...fadeUp(0)}>
        <Eyebrow>AI Buildathon · GCC Engineering Teams</Eyebrow>
      </motion.div>
      <motion.div {...fadeUp(0.08)}>
        <Typography sx={{
          fontFamily: "'IBM Plex Serif', serif",
          fontSize: { xs: "2.1rem", md: "3.2rem", lg: "3.8rem" },
          fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.05,
          color: INK, mb: 3, maxWidth: 860,
        }}>
          Don't pilot AI.{" "}
          <Box component="em" sx={{
            fontStyle: "normal",
            background: `linear-gradient(120deg, ${ACCENT}, ${ACCENT2})`,
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          }}>
            Ship with it.
          </Box>
        </Typography>
      </motion.div>
      <motion.div {...fadeUp(0.14)}>
        <Body sx={{ fontSize: { xs: "1.05rem", md: "1.15rem" }, mb: 4, maxWidth: 620 }}>
          We embed with your engineering team for a day — and leave behind a working agent,
          real code, and the patterns to keep going on your own.
        </Body>
      </motion.div>
      <motion.div {...fadeUp(0.2)}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={3.5}>
          <BookBtn label="Book a scoping call" />
          <Button
            variant="outlined"
            onClick={() => document.getElementById("stack").scrollIntoView({ behavior: "smooth" })}
            sx={{
              fontFamily: MONO, fontSize: "13.5px", px: 3, py: 1.4,
              borderRadius: "10px", color: INK, borderColor: LINE,
              textTransform: "none", boxShadow: "none",
              "&:hover": { borderColor: ACCENT, color: ACCENT, background: "transparent", boxShadow: "none" },
            }}
          >
            See the stack we teach
          </Button>
        </Stack>
      </motion.div>
      <motion.div {...fadeUp(0.25)}>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}
          sx={{ pt: 2, borderTop: `1px dashed ${LINE}` }}>
          {["Claude Code", "Agent SDK", "MCP"].map(p => (
            <Box key={p} sx={{
              border: `1px solid ${LINE}`, px: 1.2, py: 0.4,
              borderRadius: "9999px", fontFamily: MONO, fontSize: "12px", color: INK2,
            }}>{p}</Box>
          ))}
          <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: INK3 }}>
            The same stack Anthropic ships in production.
          </Typography>
        </Stack>
      </motion.div>
      <motion.div {...fadeUp(0.3)}>
        <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: INK3, letterSpacing: "0.1em", textTransform: "uppercase", mt: 4, mb: 1.25 }}>
          $ live example
        </Typography>
        <Box sx={{
          background: "#0d0e12", border: `1px solid ${LINE}`,
          borderRadius: R, p: "18px 22px", fontFamily: MONO, fontSize: "13.5px",
          color: INK2, overflowX: "auto", maxWidth: 640,
        }}>
          <Box component="span" sx={{ color: ACCENT2 }}>claude</Box>
          {" /plugin install "}
          <Box component="span" sx={{ color: GOOD }}>acme/eng-stack</Box><br />
          <Box component="span" sx={{ color: ACCENT2 }}>claude</Box>
          {" /skill "}
          <Box component="span" sx={{ color: ACCENT }}>on-call-triage</Box>
          {" --repo "}
          <Box component="span" sx={{ color: GOOD }}>apps/payments</Box><br />
          <Box component="span" sx={{ color: INK3 }}>{"# > 3 subagents dispatched. ticket linked. PR opened in 6 minutes."}</Box>
        </Box>
      </motion.div>
    </Container>
  </Box>
);

// ─── 2. GAP ───────────────────────────────────────────────────────────────────
const ladderRows = [
  { stage: "0",   name: "Chat panel",        looks: "Engineers paste code into a web UI.",                                   missing: "Copy-paste engineering. Nothing persists.",                    target: false },
  { stage: "1",   name: "Inline assist",     looks: "Autocomplete and small edits in the IDE.",                             missing: "Single-file thinking. No agents, no memory.",                  target: false },
  { stage: "2",   name: "Agentic loop",      looks: "Claude Code reads, edits, tests across the repo.",                     missing: "No team standards, no reusable skills, no governance.",         target: false },
  { stage: "3 →", name: "Engineered stack",  looks: "Subagents, hooks, skills, plugins, MCP, CLAUDE.md.",                   missing: "Org-wide rollout not done. No shipped agent products.",         target: true  },
  { stage: "4 →", name: "Agent products",    looks: "Custom agents shipped via the SDK to customers and internal users.",    missing: "Your competitive advantage lives here.",                       target: true  },
];

const GapSection = () => (
  <Box sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
    <Container maxWidth="lg">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>The gap we close</Eyebrow>
        <SectionHead maxWidth={640}>
          Most engineering orgs are stuck at "ChatGPT in a tab."
        </SectionHead>
      </motion.div>
      <motion.div {...fadeUp(0.1)}>
        {/* Legend */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: ACCENT, opacity: 0.6 }} />
          <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: INK3, letterSpacing: "0.08em" }}>
            → Where we take you
          </Typography>
        </Box>
        <Box sx={{ border: `1px solid ${LINE}`, borderRadius: R, overflow: "hidden", background: BG2 }}>
          <Box sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "90px 200px 1fr 1fr",
            gap: 2, p: "12px 20px",
            background: "rgba(255,255,255,0.045)",
            borderBottom: `1px solid ${LINE}`,
          }}>
            {["Stage", "What it is", "What it looks like", "The ceiling"].map(h => (
              <Typography key={h} sx={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase", color: INK3 }}>
                {h}
              </Typography>
            ))}
          </Box>
          {ladderRows.map((row, i) => (
            <Box key={i} sx={{
              display: { xs: "flex", md: "grid" },
              flexDirection: "column",
              gridTemplateColumns: { md: "90px 200px 1fr 1fr" },
              gap: { xs: 1, md: 2 },
              p: { xs: "16px 16px", md: "18px 20px" },
              m: { xs: "6px 8px", md: 0 },
              borderRadius: { xs: "10px", md: 0 },
              border: { xs: `1px solid ${row.target ? ACCENT + "40" : LINE}`, md: "none" },
              borderTop: { xs: "none", md: `1px solid ${LINE}` },
              background: row.target ? `linear-gradient(90deg, ${ACCENT_SOFT}, transparent)` : "transparent",
              transition: "background 0.2s",
              "&:hover": { background: row.target ? "linear-gradient(90deg, rgba(226,104,74,0.2), transparent)" : "rgba(255,255,255,0.015)" },
            }}>
              <Typography sx={{ fontFamily: MONO, fontSize: "13px", letterSpacing: "0.06em", fontWeight: 600, color: row.target ? ACCENT2 : ACCENT }}>{row.stage}</Typography>
              <Typography sx={{ fontWeight: 600, color: INK, fontSize: "15px" }}>{row.name}</Typography>
              <Typography sx={{ color: INK2, fontSize: "14.5px", lineHeight: 1.6 }}>{row.looks}</Typography>
              <Typography sx={{ color: INK2, fontSize: "14.5px", lineHeight: 1.6 }}>{row.missing}</Typography>
            </Box>
          ))}
        </Box>
      </motion.div>
    </Container>
  </Box>
);

// ─── 3. THE STACK — 3-col grid ────────────────────────────────────────────────
const stackTiles = [
  { num: "01", name: "The Agentic Coding Loop",               desc: "Claude Code reads your repo, plans changes across files, runs your tests and tools, reads the output, and iterates — interactively or fully autonomously.", group: "core" },
  { num: "02", name: "Subagents — Specialists in Parallel",   desc: "Child agents with their own system prompts, tool restrictions, and model choice. Run several at once for research, code review, security audits, and migrations — each in its own context window.", group: "core" },
  { num: "03", name: "Hooks — Lifecycle Automation",          desc: "Scripts that fire on session start, tool use, file edits, and permission requests. Auto-format. Block destructive commands. Scan for secrets. Audit-log every action. That's what makes org-wide rollout defensible.", group: "core" },
  { num: "04", name: "Skills — Your House Style, Encoded",    desc: "Model-invoked workflows packaged as markdown with frontmatter. Claude picks them up automatically based on the task. Encode your review checklist and migration patterns once — every engineer picks it up automatically.", group: "core" },
  { num: "05", name: "Slash Commands — Standardised Recipes", desc: "Recurring prompts as named commands with arguments: /release-notes, /repro-issue, /migrate-table. Living in your repo, reviewed in PRs, shared across the team.", group: "core" },
  { num: "06", name: "Plugins & Marketplace",                 desc: "Bundle skills, commands, hooks, and MCP servers into versioned plugins. Distribute through a private marketplace your platform team owns. New engineers get the org's full agent stack on day one.", group: "core" },
  { num: "07", name: "MCP — Connective Tissue",               desc: "Connect Claude Code to the tools your team already uses: source control, project management, observability, knowledge bases, internal APIs — with permissions reviewed by your security lead.", group: "core" },
  { num: "08", name: "CLAUDE.md — Project Memory",            desc: "Your repo's instruction file: build commands, code style, architectural decisions, runbook links. Plus an auto-memory layer that captures learnings as Claude works.", group: "core" },
  { num: "09", name: "Agent SDK — From Tool to Product",      desc: "How you go from using Claude Code internally to shipping agent-powered products to customers. Sessions, memory, permissions, multi-agent orchestration — Python or TypeScript.", group: "infra" },
  { num: "10", name: "IDE, Headless & CI",                    desc: "VS Code, JetBrains, terminal, desktop, browser, GitHub Actions, Slack, and any pipeline that can shell out. We teach the trade-offs so the right work happens in the right place.", group: "infra" },
  { num: "11", name: "Permissions & Sandbox",                 desc: "Rules for every tool, file pattern, and bash command class. OS-level sandboxing for the Bash tool. The difference between an agent that runs in production and one that gets shut down after the first incident.", group: "infra" },
  { num: "12", name: "Managed Governance — Org-Wide Policy",  desc: "Managed settings via MDM. Required login. Network controls. MCP whitelists. Audit hooks streaming to your observability stack. The posture that makes a hundred-engineer rollout defensible.", group: "infra" },
];

// Mobile: sticky scroll through all tiles one by one
const StackMobileScroll = () => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  useEffect(() => {
    return scrollYProgress.on("change", v => {
      const idx = Math.max(0, Math.min(stackTiles.length - 1, Math.round(v * (stackTiles.length - 1))));
      setActiveIndex(idx);
    });
  }, [scrollYProgress]);

  const tile = stackTiles[activeIndex];

  return (
    <Box
      ref={containerRef}
      sx={{ position: "relative", height: `calc(100vh + ${(stackTiles.length - 1) * 180}px)` }}
    >
      <Box sx={{
        position: "sticky", top: "56px",
        height: "calc(100vh - 56px)",
        display: "flex", flexDirection: "column", justifyContent: "center",
        px: 2.5, py: 4,
      }}>
        {/* Progress bar */}
        <Stack direction="row" spacing={0.5} mb={3.5}>
          {stackTiles.map((_, i) => (
            <Box key={i} sx={{
              height: 3, borderRadius: 2,
              flex: i === activeIndex ? 3 : 1,
              background: i === activeIndex ? ACCENT : i < activeIndex ? `${ACCENT}45` : LINE,
              transition: "all 0.35s",
            }} />
          ))}
        </Stack>

        {/* Group label */}
        <Typography sx={{ fontFamily: MONO, fontSize: "10px", color: INK3, letterSpacing: "0.12em", textTransform: "uppercase", mb: 2 }}>
          {tile.group === "core" ? "Core workflow" : "Infrastructure & governance"}
        </Typography>

        {/* Tile card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.3 }}
          >
            <Box sx={{ p: 3, background: BG2, border: `1px solid ${LINE}`, borderRadius: R }}>
              <Typography sx={{ fontFamily: MONO, color: ACCENT, fontSize: "14px", mb: 1.25 }}>
                {tile.num}
              </Typography>
              <Typography sx={{ fontWeight: 600, color: INK, fontSize: "17px", mb: 1, letterSpacing: "-0.005em" }}>
                {tile.name}
              </Typography>
              <Typography sx={{ color: INK2, fontSize: "14px", lineHeight: 1.7 }}>
                {tile.desc}
              </Typography>
            </Box>
          </motion.div>
        </AnimatePresence>

        {/* Counter */}
        <Typography sx={{ fontFamily: MONO, fontSize: "12px", color: INK3, mt: 3 }}>
          {activeIndex + 1} / {stackTiles.length}
        </Typography>
      </Box>
    </Box>
  );
};

const StackSection = () => (
  <Box id="stack" sx={{ borderBottom: `1px solid ${LINE}`, background: BG }}>
    {/* Header — always visible */}
    <Box sx={{ pt: { xs: 8, md: 13 }, pb: { xs: 3, md: 4 } }}>
      <Container maxWidth="lg">
        <motion.div {...fadeUp(0)}>
          <Eyebrow>The Claude Code stack, in depth</Eyebrow>
          <SectionHead maxWidth={680}>
            Most AI workshops stop at the chat panel. We teach the twelve layers below it.
          </SectionHead>
          <Body sx={{ mb: { xs: 2, md: 3 } }}>
            By the end of the day, your engineers run all twelve. Not watch. Run.
          </Body>
        </motion.div>
      </Container>
    </Box>

    {/* Mobile: sticky one-by-one scroll */}
    <Box sx={{ display: { xs: "block", md: "none" } }}>
      <StackMobileScroll />
    </Box>

    {/* Desktop: grouped 4-col grid */}
    <Box sx={{ display: { xs: "none", md: "block" }, pb: 13 }}>
      <Container maxWidth="lg">
        {["core", "infra"].map((group) => (
          <Box key={group} sx={{ mb: group === "core" ? 4 : 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: INK3, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {group === "core" ? "Core workflow" : "Infrastructure & governance"}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", background: LINE }} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
              {stackTiles.filter(t => t.group === group).map((tile, i) => (
                <motion.div key={tile.num} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}>
                  <Box sx={{
                    p: "22px 24px", background: BG2, border: `1px solid ${LINE}`, borderRadius: R,
                    height: "100%", transition: "border-color 0.2s, transform 0.2s",
                    "&:hover": { borderColor: ACCENT, transform: "translateY(-2px)" },
                  }}>
                    <Typography sx={{ fontFamily: MONO, color: ACCENT, fontSize: "14px", mb: 1.25 }}>{tile.num}</Typography>
                    <Typography sx={{ fontWeight: 600, color: INK, mb: 0.75, fontSize: "15.5px", letterSpacing: "-0.005em" }}>{tile.name}</Typography>
                    <Typography sx={{ color: INK2, fontSize: "13.5px", lineHeight: 1.65 }}>{tile.desc}</Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Box>
        ))}
      </Container>
    </Box>
  </Box>
);

// ─── 4. WHO IT'S FOR ──────────────────────────────────────────────────────────
const personas = [
  "CTO · Fintech GCC",
  "VP Engineering · Retail GCC",
  "CIO · Healthcare GCC",
  "VP Product · SaaS GCC",
  "VP Engineering · Logistics GCC",
];

const WhoItsFor = () => (
  <Box sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
    <Container maxWidth="lg">
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: { xs: 5, md: 8 },
        alignItems: "center",
      }}>
        {/* Left: heading + body + link */}
        <motion.div {...fadeUp(0)}>
          <Eyebrow>Built for</Eyebrow>
          <SectionHead maxWidth={520}>
            Built for the GCC Leader Who's{" "}
            <GradientText>Done Watching Demos</GradientText>
          </SectionHead>
          <Body sx={{ mb: 3, maxWidth: 480 }}>
            Not another workshop your team forgets by Thursday. You need evidence —
            AI working on your actual data, inside your real systems, validated on
            production-grade infrastructure.
          </Body>
          <Typography
            onClick={() => document.getElementById("agenda") ? document.getElementById("agenda").scrollIntoView({ behavior: "smooth" }) : null}
            sx={{
              fontFamily: MONO, fontSize: "13px", color: ACCENT,
              cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 0.75,
              "&:hover": { opacity: 0.75 },
            }}
          >
            See what a day looks like →
          </Typography>
        </motion.div>

        {/* Right: persona chips in a tighter grid */}
        <motion.div {...fadeUp(0.1)}>
          <Box sx={{
            display: "flex", flexWrap: "wrap", gap: 1.5,
          }}>
            {personas.map(p => (
              <Chip key={p} label={p} sx={{
                border: `1px solid rgba(226, 104, 74, 0.35)`,
                background: "rgba(226, 104, 74, 0.07)",
                color: INK, fontFamily: MONO, fontSize: "12.5px",
                height: 36, borderRadius: "9999px", letterSpacing: "0.01em",
              }} />
            ))}
          </Box>
        </motion.div>
      </Box>
    </Container>
  </Box>
);

// ─── 5. BUILD TRACKS ──────────────────────────────────────────────────────────
const tracks = [
  {
    id: 1, label: "Track 1", color: ACCENT,
    title: "Internal Tool Sprint",
    tagline: "Turn one painful manual process into a running agent — today.",
    desc: "Every GCC has a process that runs on human patience: invoice reconciliation, lead routing, compliance checks, support triage. Your team identifies one, our mentors shape it into an executable architecture, and you build it in the session.",
    bestFor: "Operations, Finance, Shared Services, Sales Support",
    example: {
      prompt: "We process 200 invoice PDFs every month. Extract line items, flag duplicates, check against POs, and route exceptions to finance.",
      result: "A working agent that reads your PDFs, cross-checks line items against POs, and routes exceptions to finance — same day.",
    },
    agents: [
      { name: "Extractor Agent", role: "Reads PDFs, parses tables, extracts structured data" },
      { name: "Matcher Agent",   role: "Cross-references line items against PO numbers and flags discrepancies" },
      { name: "Router Agent",    role: "Packages exceptions with context and sends to finance lead" },
    ],
  },
  {
    id: 2, label: "Track 2", color: PURPLE,
    title: "Product Feature Lab",
    tagline: "AI that ships inside your product, not in a separate demo environment.",
    desc: "Intelligent search, auto-triage, context-aware summaries, smart routing — built directly inside your existing codebase. Not a bolted-on integration. Native AI your team will own and extend.",
    bestFor: "Product Engineering, Platform Teams, Customer Success",
    example: {
      prompt: "We get 10 RFPs a month. Each takes 40 hours to respond to. Build something that reads the RFP, pulls from our past proposals, and drafts a first response.",
      result: "A complete draft response built from your actual past proposals — gaps flagged, ready for your team to review in minutes.",
    },
    agents: [
      { name: "Parser Agent",    role: "Reads RFP PDF, extracts questions, deadlines, and compliance requirements" },
      { name: "Retrieval Agent", role: "Searches past proposals, case studies, and product docs for relevant content" },
      { name: "Composer Agent",  role: "Assembles answers into formatted response, flags missing info" },
      { name: "Review Agent",    role: "Checks for consistency, tone, and deadline conflicts before handoff" },
    ],
  },
  {
    id: 3, label: "Track 3", color: CYAN,
    title: "AI-Accelerated Engineering",
    tagline: "Double your junior engineers' output without doubling headcount.",
    desc: "Real tickets from your backlog. Real features your product needs. No tutorial exercises. Junior engineers build alongside senior AI mentors and leave knowing the patterns to do it again independently.",
    bestFor: "Engineering Managers, VPs of Engineering, Tech Leads",
    example: {
      prompt: "Our junior dev needs a live dashboard pulling sales, support, and uptime data. She has never built one.",
      result: "A deployed dashboard with live charts from your APIs — built by your junior dev, without a senior engineer in the loop.",
    },
    agents: [
      { name: "Planner Agent", role: "Breaks dashboard into components, maps API endpoints" },
      { name: "Coder Agent",   role: "Writes React + API calls, handles errors and loading states" },
      { name: "Polish Agent",  role: "Adds styling, responsive layout, and auto-refresh logic" },
    ],
  },
  {
    id: 4, label: "Track 4", color: "#F472B6",
    title: "Agentic System Design",
    tagline: "For senior teams ready to build systems that run without supervision.",
    desc: "Multi-step agent pipelines with persistent memory, conditional branching, tool integration, and failure recovery. For organisations where the next AI investment needs to handle complexity at scale.",
    bestFor: "Senior Engineers, Solution Architects, Platform Teams",
    example: {
      prompt: "Predict which customers will churn and trigger retention playbooks before they cancel.",
      result: "A daily pipeline that scores churn risk per account and fires the right retention action automatically — email, discount, or CS alert.",
    },
    agents: [
      { name: "Signal Agent", role: "Ingests product usage, ticket sentiment, payment status" },
      { name: "Scorer Agent", role: "Calculates churn risk, ranks accounts by probability" },
      { name: "Action Agent", role: "Triggers emails, discounts, or human alerts per risk tier" },
      { name: "Learn Agent",  role: "Tracks which actions saved accounts, refines future recommendations" },
    ],
  },
];

const BuildTracks = () => {
  const [active, setActive] = useState(0);
  const t = tracks[active];

  return (
    <Box id="themes" sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
      <Container maxWidth="lg">
        <motion.div {...fadeUp(0)}>
          <Eyebrow>4 Build Tracks</Eyebrow>
          <SectionHead maxWidth={560}>
            You Pick the Problem. <GradientText>We Build It With You.</GradientText>
          </SectionHead>
          <Body sx={{ mb: 5, maxWidth: 580 }}>
            Each track is calibrated to a different team capability level and business priority.
            Most GCCs run one track per Buildathon. Some run two in parallel.
          </Body>
        </motion.div>

        {/* Tab selector */}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mb={4} flexWrap="wrap">
          {tracks.map((tr, i) => (
            <Button key={tr.id} onClick={() => setActive(i)}
              sx={{
                fontFamily: MONO, fontSize: "12.5px", px: 2.5, py: 1.2,
                borderRadius: "10px", textTransform: "none", border: `1px solid`,
                ...(active === i
                  ? { background: `${tr.color}18`, borderColor: tr.color, color: tr.color }
                  : { background: "transparent", borderColor: LINE, color: INK3,
                      "&:hover": { borderColor: tr.color, color: tr.color, background: `${tr.color}08` } }),
              }}>
              {tr.label}: {tr.title}
            </Button>
          ))}
        </Stack>

        {/* Detail panel */}
        <motion.div key={active} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <Box sx={{
            display: "grid", gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
            gap: 3,
          }}>
            {/* Left: description */}
            <Box sx={{ p: 3.5, border: `1px solid ${t.color}30`, borderRadius: R, background: `${t.color}08` }}>
              <Typography sx={{ fontWeight: 700, color: INK, fontSize: "1.35rem", mb: 0.75 }}>{t.title}</Typography>
              <Typography sx={{ color: t.color, fontWeight: 600, mb: 2, fontSize: "14.5px" }}>{t.tagline}</Typography>
              <Typography sx={{ color: INK2, fontSize: "14.5px", lineHeight: 1.75, mb: 2.5 }}>{t.desc}</Typography>
              <Box sx={{ border: `1px solid ${LINE}`, borderRadius: 2, p: 2, mb: 3 }}>
                <Typography sx={{ fontFamily: MONO, fontSize: "10.5px", color: INK3, letterSpacing: "0.1em", textTransform: "uppercase", mb: 0.5 }}>Best For</Typography>
                <Typography sx={{ color: INK, fontSize: "14px" }}>{t.bestFor}</Typography>
              </Box>
              <Typography sx={{ fontFamily: MONO, fontSize: "10.5px", color: INK3, letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.5 }}>Agent Team</Typography>
              <Stack spacing={1}>
                {t.agents.map((ag, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", p: 1.5, borderRadius: 1.5, background: "rgba(255,255,255,0.03)", border: `1px solid ${LINE}` }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: t.color, flexShrink: 0, mt: 0.8 }} />
                    <Box>
                      <Typography sx={{ color: INK, fontWeight: 600, fontSize: "12.5px" }}>{ag.name}</Typography>
                      <Typography sx={{ color: INK3, fontSize: "12px", lineHeight: 1.5 }}>{ag.role}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Right: pipeline infographic */}
            <Box sx={{ p: 3.5, border: `1px solid ${LINE}`, borderRadius: R, background: BG2, display: "flex", flexDirection: "column", gap: 3 }}>

              {/* You Say */}
              <Box>
                <Typography sx={{ fontFamily: MONO, fontSize: "10.5px", color: INK3, letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.25 }}>You say</Typography>
                <Box sx={{ p: 2, borderRadius: 2, background: "rgba(255,255,255,0.03)", border: `1px solid ${LINE}`, borderLeft: `3px solid ${INK3}` }}>
                  <Typography sx={{ color: "#CBD5E1", fontStyle: "italic", fontSize: "14px", lineHeight: 1.75 }}>"{t.example.prompt}"</Typography>
                </Box>
              </Box>

              {/* Arrow down */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${t.color}60, transparent)` }} />
                <Typography sx={{ fontFamily: MONO, fontSize: "11px", color: t.color, letterSpacing: "0.08em" }}>agent pipeline</Typography>
                <Box sx={{ flex: 1, height: "1px", background: `linear-gradient(270deg, ${t.color}60, transparent)` }} />
              </Box>

              {/* Agent flow */}
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { sm: "center" }, flexWrap: "wrap", gap: 1 }}>
                {t.agents.map((ag, i) => (
                  <React.Fragment key={i}>
                    <Box sx={{
                      flex: 1, minWidth: 0,
                      p: "10px 14px", borderRadius: "10px",
                      background: `${t.color}10`, border: `1px solid ${t.color}35`,
                      textAlign: "center",
                    }}>
                      <Typography sx={{ color: t.color, fontWeight: 700, fontSize: "12px", mb: 0.25 }}>{ag.name}</Typography>
                      <Typography sx={{ color: INK3, fontSize: "11px", lineHeight: 1.4 }}>{ag.role}</Typography>
                    </Box>
                    {i < t.agents.length - 1 && (
                      <Typography sx={{ color: t.color, fontSize: "16px", flexShrink: 0, display: { xs: "none", sm: "block" } }}>›</Typography>
                    )}
                  </React.Fragment>
                ))}
              </Box>

              {/* Output */}
              <Box>
                <Typography sx={{ fontFamily: MONO, fontSize: "10.5px", color: t.color, letterSpacing: "0.1em", textTransform: "uppercase", mb: 1 }}>What ships</Typography>
                <Box sx={{ p: 2, borderRadius: 2, background: `${t.color}08`, border: `1px solid ${t.color}30`, display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                  <CheckCircleOutlineIcon sx={{ color: t.color, fontSize: "18px", flexShrink: 0, mt: "2px" }} />
                  <Typography sx={{ color: INK, fontSize: "14px", lineHeight: 1.7 }}>{t.example.result}</Typography>
                </Box>
              </Box>

            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

// ─── 6. TIMELINE (The Agenda) ─────────────────────────────────────────────────
const timelineSteps = [
  { time: "Morning",        title: "Scoping the Build",        desc: "We isolate one high-friction process. Our mentors translate the business problem into an architecture your engineers can execute during the day.",                                    output: "Scoped problem. Agreed architecture. Teams formed." },
  { time: "Late Morning",   title: "Build Sprint",             desc: "Your engineers write the code. Our mentors are embedded — catching architectural decisions that would cost you weeks in production, and surfacing the questions that only matter on real data.", output: "Working prototype on your actual systems." },
  { time: "Post-Lunch",     title: "Hardening & Demo Prep",    desc: "Teams stress-test against edge cases from your real data. Mentors help articulate the production path — what it takes to go from prototype to something ops runs every day.",            output: "Tested prototype. Production scoping notes." },
  { time: "Mid-Afternoon",  title: "Leadership Demo",          desc: "Each team presents to you — live output, not slides. You evaluate on business value, feasibility, and ROI. Our leads give their honest read on what production would require.",             output: "A clear winner. A ranked list of next bets." },
  { time: "Late Afternoon", title: "Production Pathfinding",   desc: "The prototype proved the concept. We walk through build scope, team structure, and whether the right move is to build internally, extend your team, or partner.",                         output: "Scoped proposal or introduction to vetted engineers." },
  { time: "End of Day",     title: "Continuity Kit",           desc: "Every engineer leaves with patterns, tooling setup, and architecture notes to keep building without us. The goal is capability transfer — not dependency.",                               output: "Starter kit. Shared channel. Capability stays in-house." },
];

const Timeline = () => (
  <Box id="agenda" sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
    <Container maxWidth="lg">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>The Agenda · Internal Tool Sprint</Eyebrow>
        <SectionHead maxWidth={520}>
          What Happens in a Day for <GradientText>Internal Tool Sprint</GradientText>
        </SectionHead>
      </motion.div>

      <Box sx={{ position: "relative", mt: 6 }}>
        {/* Vertical line */}
        <Box sx={{
          display: { xs: "none", md: "block" },
          position: "absolute", left: "50%", top: 0, bottom: 0, width: "1px",
          background: `linear-gradient(180deg, transparent, ${ACCENT}66 20%, ${PURPLE}66 80%, transparent)`,
          transform: "translateX(-50%)",
        }} />

        <Stack spacing={5}>
          {timelineSteps.map((step, i) => {
            const isLeft = i % 2 === 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: isLeft ? -24 : 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.55, delay: 0.05 }}
              >
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 64px 1fr" }, gap: { xs: 2, md: 3 }, alignItems: "center" }}>
                  {/* Left cell */}
                  <Box sx={{ textAlign: { xs: "left", md: isLeft ? "right" : "left" }, order: { xs: 0, md: isLeft ? 0 : 2 } }}>
                    {isLeft && (
                      <Box sx={{ p: "20px 24px", background: BG2, border: `1px solid ${LINE}`, borderRadius: R }}>
                        <Typography sx={{ fontFamily: MONO, color: ACCENT, fontSize: "11px", letterSpacing: "0.1em", mb: 0.75 }}>{step.time}</Typography>
                        <Typography sx={{ fontWeight: 600, color: INK, fontSize: "17px" }}>{step.title}</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Center dot */}
                  <Box sx={{ display: "flex", justifyContent: "center", order: 1 }}>
                    <Box sx={{
                      width: 44, height: 44, borderRadius: "50%",
                      background: `linear-gradient(135deg, ${ACCENT}, ${PURPLE})`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, boxShadow: `0 0 18px ${ACCENT}66`,
                      border: "2px solid rgba(255,255,255,0.08)",
                    }}>
                      <Typography sx={{ color: "white", fontWeight: 700, fontFamily: MONO, fontSize: "13px" }}>{i + 1}</Typography>
                    </Box>
                  </Box>

                  {/* Right cell */}
                  <Box sx={{ order: { xs: 2, md: isLeft ? 2 : 0 } }}>
                    {!isLeft ? (
                      <Box sx={{ p: "20px 24px", background: BG2, border: `1px solid ${LINE}`, borderRadius: R }}>
                        <Typography sx={{ fontFamily: MONO, color: ACCENT, fontSize: "11px", letterSpacing: "0.1em", mb: 0.75 }}>{step.time}</Typography>
                        <Typography sx={{ fontWeight: 600, color: INK, fontSize: "17px" }}>{step.title}</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: { xs: "block", md: "flex" }, justifyContent: "flex-start" }}>
                        <OutputBadge text={step.output} />
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Output badge below right-side cards */}
                {!isLeft && (
                  <Box sx={{ display: { xs: "none", md: "flex" }, justifyContent: "flex-end", mt: 1.5, pr: "calc(64px + 24px)" }}>
                    <OutputBadge text={step.output} />
                  </Box>
                )}
              </motion.div>
            );
          })}
        </Stack>
      </Box>
    </Container>
  </Box>
);

// ─── 8. WHY IT WORKS ──────────────────────────────────────────────────────────
const reasons = [
  { icon: <StorageIcon fontSize="large" />,  title: "Your data. Your systems.", desc: "We build on your actual infrastructure — legacy schemas, messy formats, undocumented APIs. Never sample data." },
  { icon: <VerifiedIcon fontSize="large" />, title: "Mentors with live systems.", desc: "Every mentor has a GCC agentic system running in production today. They've debugged the failures you haven't hit yet." },
  { icon: <GroupsIcon fontSize="large" />,   title: "Your engineers write the code.", desc: "We're present, not prescriptive. Every line belongs to your team, and the next build happens without us." },
  { icon: <FactCheckIcon fontSize="large" />, title: "The day is the due diligence.", desc: "If it works on your stack in the session, you already know it works with your team. No separate POC needed." },
];

const WhyItWorks = () => (
  <Box sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
    <Container maxWidth="lg">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>Why this model works</Eyebrow>
        <SectionHead maxWidth={520}>
          Why GCC Leaders Choose This <GradientText>Over Another Pilot</GradientText>
        </SectionHead>
      </motion.div>
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
        gap: 2, mt: 3,
      }}>
        {reasons.map((r, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.45, delay: i * 0.07 }}>
            <Box sx={{
              p: 3, background: BG2, border: `1px solid ${LINE}`, borderRadius: R,
              display: "flex", gap: 2.5, alignItems: "flex-start",
              height: "100%", transition: "border-color 0.2s",
              "&:hover": { borderColor: `${ACCENT}50` },
            }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
                background: "rgba(226,104,74,0.10)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: ACCENT,
              }}>
                {r.icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: INK, fontSize: "15.5px", mb: 0.6, letterSpacing: "-0.005em" }}>{r.title}</Typography>
                <Typography sx={{ color: INK2, fontSize: "14px", lineHeight: 1.7 }}>{r.desc}</Typography>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Container>
  </Box>
);

// ─── 9. GOVERNANCE ────────────────────────────────────────────────────────────
const govItems = [
  { icon: <LockIcon />,                title: "Permission Policy",       desc: "Allow, ask, and deny rules per tool, file pattern, and bash command class — documented and version-controlled." },
  { icon: <ShieldIcon />,              title: "OS-Level Sandboxing",     desc: "Bash tool runs in an OS sandbox where blast radius matters. No outbound access unless explicitly whitelisted." },
  { icon: <AdminPanelSettingsIcon />,  title: "MDM-Managed Settings",    desc: "Login requirements, network access, and bypass-mode locks pushed org-wide via your existing MDM." },
  { icon: <ReceiptLongIcon />,         title: "Audit Hooks",             desc: "Every tool call logged to your observability stack. Structured JSON — queryable, alertable, retainable." },
  { icon: <VpnKeyIcon />,              title: "Secrets Posture",         desc: "What lives in .env, what's in your secret manager, and what Claude is never allowed to read — agreed before day one." },
  { icon: <StorageIcon />,             title: "Data Residency Review",   desc: "Model selection and data handling reviewed with your security team before any agent touches production." },
  { icon: <WarningAmberIcon />,        title: "Incident Playbook",       desc: "A documented response protocol for 'the agent did something unexpected' — not an afterthought." },
];

const GovernanceSection = () => (
  <Box sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
    <Container maxWidth="lg">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>Governance, security & the boring stuff that matters</Eyebrow>
        <SectionHead maxWidth={660}>
          Most adoption programs skip this section. We lead with it.
        </SectionHead>
        <Body sx={{ mb: 5, maxWidth: 560 }}>
          If your security and compliance teams aren't in the room, the project hasn't started.
        </Body>
      </motion.div>
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
        gap: 2,
      }}>
        {govItems.map((item, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.45, delay: i * 0.06 }}>
            <Box sx={{
              p: 3, background: BG2, border: `1px solid ${LINE}`, borderRadius: R,
              display: "flex", gap: 2.5, alignItems: "flex-start",
              height: "100%", transition: "border-color 0.2s",
              "&:hover": { borderColor: `${ACCENT}45` },
            }}>
              <Box sx={{
                width: 42, height: 42, borderRadius: "10px", flexShrink: 0,
                background: "rgba(226,104,74,0.09)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: ACCENT,
              }}>
                {item.icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: INK, fontSize: "15px", mb: 0.5, letterSpacing: "-0.005em" }}>{item.title}</Typography>
                <Typography sx={{ color: INK2, fontSize: "13.5px", lineHeight: 1.7 }}>{item.desc}</Typography>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Container>
  </Box>
);

// ─── 10. MENTORS ──────────────────────────────────────────────────────────────
const mentors = [
  {
    name: "Srikanth S",
    title: "AI Systems Architect",
    bio: "8+ years building enterprise data pipelines and agentic systems across fintech and healthcare GCCs. Led AI adoption for a 200-person engineering org in Bangalore.",
    tags: ["Claude Code", "Agent SDK", "Fintech"],
  },
  {
    name: "Karthik T",
    title: "Senior AI Engineer",
    bio: "Shipped production multi-agent systems for logistics and retail GCCs. Contributor to open-source MCP tooling. Specialises in agentic workflow design and governance.",
    tags: ["MCP", "Multi-agent", "Logistics"],
  },
  {
    name: "Sahas Nanabala",
    title: "AI Product Engineer",
    bio: "Built AI-native internal tools for SaaS and e-commerce GCCs. Focuses on capability transfer — teams he works with keep building autonomously after the engagement ends.",
    tags: ["Internal Tools", "Agent SDK", "SaaS"],
  },
];

const Mentors = () => (
  <Box sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
    <Container maxWidth="lg">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>The Mentors</Eyebrow>
        <SectionHead maxWidth={600}>
          Engineers Who've Shipped AI in{" "}
          <GradientText from={PURPLE} to={CYAN}>GCC Production</GradientText>
        </SectionHead>
        <Body sx={{ mb: 5, maxWidth: 600 }}>
          Every mentor has a live agentic system running in a GCC enterprise today — fintech, retail, healthcare, logistics.
          Sourced exclusively from Crewscale's vetted AI engineering network.
        </Body>
      </motion.div>

      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
        gap: 3,
      }}>
        {mentors.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.5, delay: i * 0.1 }}>
            <Box sx={{
              p: 3, background: BG2, border: `1px solid ${LINE}`, borderRadius: R,
              height: "100%", transition: "border-color 0.2s",
              "&:hover": { borderColor: `${PURPLE}60` },
            }}>
              {/* Photo placeholder */}
              <Box sx={{
                width: 72, height: 72, borderRadius: "50%", mb: 2.5,
                background: `linear-gradient(135deg, ${PURPLE}40, ${ACCENT}30)`,
                border: `2px solid ${PURPLE}40`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Typography sx={{ fontFamily: MONO, color: PURPLE, fontSize: "22px", fontWeight: 700 }}>
                  {m.name.charAt(0)}
                </Typography>
              </Box>

              <Typography sx={{ fontWeight: 700, color: INK, fontSize: "17px", mb: 0.4 }}>{m.name}</Typography>
              <Typography sx={{ fontFamily: MONO, color: ACCENT, fontSize: "11px", letterSpacing: "0.08em", mb: 1.5 }}>{m.title}</Typography>
              <Typography sx={{ color: INK2, fontSize: "14px", lineHeight: 1.7, mb: 2.5 }}>{m.bio}</Typography>

              {/* Tags */}
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {m.tags.map(tag => (
                  <Box key={tag} sx={{
                    border: `1px solid ${LINE}`, px: 1.25, py: 0.4,
                    borderRadius: "9999px", fontFamily: MONO, fontSize: "11px", color: INK3,
                  }}>{tag}</Box>
                ))}
              </Stack>
            </Box>
          </motion.div>
        ))}
      </Box>
    </Container>
  </Box>
);

// ─── 11. SOCIAL PROOF ─────────────────────────────────────────────────────────
const proofImages = [
  { src: pic1, alt: "Buildathon session — team working" },
  { src: pic2, alt: "Buildathon session — mentors embedded" },
  { src: pic3, alt: "Buildathon session — demo day" },
  { src: pic4, alt: "Buildathon session — GCC office" },
];

const SocialProof = () => (
  <Box sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG, overflow: "hidden" }}>
    <Container maxWidth="lg">
      <motion.div {...fadeUp(0)}>
        <Eyebrow>In the room</Eyebrow>
        <SectionHead maxWidth={520}>
          Buildathons Done by Our Founder
        </SectionHead>
        <Body sx={{ mb: 2, maxWidth: 560 }}>
          Organized in association with BITSAA Bangalore and Scaler Institute of Technology at Electronic City, Bengaluru
        </Body>
      </motion.div>
      <Box sx={{
        mt: 4,
        display: "flex", gap: 2,
        overflowX: "auto", pb: 2,
        scrollSnapType: "x mandatory",
        "&::-webkit-scrollbar": { height: 4 },
        "&::-webkit-scrollbar-track": { background: BG2, borderRadius: 2 },
        "&::-webkit-scrollbar-thumb": { background: LINE, borderRadius: 2 },
        cursor: "grab", "&:active": { cursor: "grabbing" },
      }}>
        {proofImages.map((img, i) => (
          <Box
            key={i}
            component="img"
            src={img.src}
            alt={img.alt}
            sx={{
              flexShrink: 0,
              width: { xs: "80vw", sm: "340px", md: "400px" },
              height: { xs: 220, md: 280 },
              scrollSnapAlign: "start",
              borderRadius: R,
              border: `1px solid ${LINE}`,
              objectFit: "cover",
              transition: "border-color 0.2s, transform 0.2s",
              "&:hover": { borderColor: ACCENT, transform: "scale(1.01)" },
            }}
          />
        ))}
      </Box>
    </Container>
  </Box>
);

// ─── 12. CTA ─────────────────────────────────────────────────────────────────
const CTASection = () => (
  <Box sx={{
    py: { xs: 10, md: 13 }, textAlign: "center",
    background: `radial-gradient(700px 350px at 50% 50%, rgba(226,104,74,0.10), transparent 60%), ${BG}`,
    borderBottom: `1px solid ${LINE}`,
  }}>
    <Container maxWidth="md">
      <motion.div {...fadeUp(0)}>
        <Eyebrow center>Talk to us</Eyebrow>
        <SectionHead center maxWidth={640} sx={{ mb: 2 }}>
          Your team is already using Claude.{" "}
          <GradientText>Help them use it like engineers.</GradientText>
        </SectionHead>
        <Body center sx={{ mb: 4.5, maxWidth: 560, fontSize: "1.05rem" }}>
          Book a 30-minute scoping call. We'll look at one of your repos with you, identify
          the three highest-leverage targets, and tell you honestly whether we're the right
          partner. No deck. No sales theatre.
        </Body>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
          <BookBtn label="Book a scoping call" size="lg" />
          <Button
            component="a"
            href="mailto:hello@beanbag.ai"
            variant="outlined"
            endIcon={<ArrowForwardIcon />}
            sx={{
              fontFamily: MONO, fontSize: "15px", px: 4, py: 1.8,
              borderRadius: "10px", color: INK, borderColor: LINE,
              textTransform: "none", boxShadow: "none",
              "&:hover": { borderColor: ACCENT, color: ACCENT, background: "transparent", boxShadow: "none" },
            }}
          >
            Email us
          </Button>
        </Stack>
      </motion.div>
    </Container>
  </Box>
);

// ─── 13. FAQ ──────────────────────────────────────────────────────────────────
const faqs = [
  { q: "Do our engineers need prior experience with Claude Code?",   a: "No. We've run buildathons with teams whose only prior exposure was the chat UI, and with teams who had been using Claude Code daily for a year. We calibrate the first session to where you actually are." },
  { q: "What languages and stacks do you support?",                  a: "Claude Code is language-agnostic. We've shipped engagements across TypeScript, Python, Go, Java, Ruby, and Rust. Bring your stack." },
  { q: "Where does the work happen?",                                a: "In your repos, on your infrastructure, behind your firewall. We do not move code out. Agent SDK products we build are deployable to your cloud or Anthropic's managed runtime — your choice." },
  { q: "How do you handle data privacy?",                            a: "We sign your DPA and NDA before kickoff. MCP integrations are configured against your scopes and tokens. We default to the most restrictive permission model and loosen only with sign-off from your security lead." },
  { q: "What happens after the buildathon?",                         a: "You own everything we built — repos, skills, plugins, agents. We stay available for 30 days of async support and offer optional quarterly upgrades as Anthropic ships new capabilities." },
  { q: "Can you train a single team first and expand later?",        a: "Yes. A scoped pilot with one squad is a common first step before a full-org rollout. We'll scope that on the alignment call." },
  { q: "Do you guarantee outcomes?",                                 a: "We guarantee working deliverables: a prototype on your data, production architecture notes, and an AI readiness assessment. We don't guarantee an agent replaces a department by Friday — anyone who promises that is selling something else." },
];

const FAQSection = () => {
  const [open, setOpen] = useState(null);
  return (
    <Box sx={{ py: { xs: 10, md: 13 }, background: BG }}>
      <Container maxWidth="lg">
        <motion.div {...fadeUp(0)}>
          <Eyebrow>FAQ</Eyebrow>
          <SectionHead maxWidth={500}>The questions we hear before kickoff.</SectionHead>
        </motion.div>
        <Box sx={{ mt: 2 }}>
          {faqs.map((faq, i) => (
            <Box key={i} sx={{ borderTop: `1px solid ${LINE}`, "&:last-child": { borderBottom: `1px solid ${LINE}` } }}>
              <Box onClick={() => setOpen(open === i ? null : i)}
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 2.5, px: 0.5, cursor: "pointer" }}>
                <Typography sx={{ fontSize: { xs: "15.5px", md: "17px" }, fontWeight: 500, color: open === i ? INK : "#C5CAD4", transition: "color 0.15s", pr: 2, lineHeight: 1.4 }}>
                  {faq.q}
                </Typography>
                <Box sx={{ fontFamily: MONO, color: ACCENT, fontSize: "22px", flexShrink: 0, lineHeight: 1, userSelect: "none" }}>
                  {open === i ? "−" : "+"}
                </Box>
              </Box>
              {open === i && (
                <Box sx={{ pb: 2.5, px: 0.5 }}>
                  <Typography sx={{ color: INK2, fontSize: "15.5px", lineHeight: 1.75, maxWidth: 720 }}>{faq.a}</Typography>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

// ─── PAGE ASSEMBLY ────────────────────────────────────────────────────────────
const BuildathonPageTesting = () => {
  useEffect(() => {
    if (window.Cal) return;
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.innerHTML = `
      (function (C, A, L) {
        let p = function (a, ar) { a.q.push(ar); };
        let d = C.document;
        C.Cal = C.Cal || function () {
          let cal = C.Cal; let ar = arguments;
          if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
          if (ar[0] === L) {
            const api = function () { p(api, arguments); };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") { cal.ns[namespace] = cal.ns[namespace] || api; p(cal.ns[namespace], ar); p(cal, ["initNamespace", namespace]); } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
      })(window, "https://app.cal.com/embed/embed.js", "init");
      Cal("init", "30-min-meeting", { origin: "https://app.cal.com" });
      Cal.ns["30-min-meeting"]("ui", { "hideEventTypeDetails": false, "layout": "month_view" });
    `;
    document.head.appendChild(script);
  }, []);

  useSEO({
    title: "AI Buildathon for GCC Enterprises | Beanbag AI",
    description: "We embed with your GCC engineering team for a focused buildathon and leave behind real products, real agents, and the muscle to keep building.",
    keywords: "AI Buildathon GCC, Claude Code, Enterprise AI, Agent SDK, MCP, Beanbag AI",
    ogTitle: "AI Buildathon by Beanbag AI — Don't pilot AI. Ship with it.",
    ogDescription: "Real products, real agents, real muscle — built inside your GCC office in one focused buildathon.",
    ogType: "website",
  });

  return (
    <Box sx={{ bgcolor: BG, color: INK }}>
      <Hero />
      <GapSection />
      <StackSection />
      <WhoItsFor />
      <BuildTracks />
      <Timeline />
      <WhyItWorks />
      <GovernanceSection />
      <Mentors />
      <SocialProof />
      <CTASection />
      <FAQSection />
    </Box>
  );
};

const BuildathonPage = BuildathonPageTesting;
export default BuildathonPage;
